"use client";

import { setupAutomaticEthereumWalletDerivation } from "@aptos-labs/derived-wallet-ethereum";
import { setupAutomaticSolanaWalletDerivation } from "@aptos-labs/derived-wallet-solana";
import { setupAutomaticSuiWalletDerivation } from "@aptos-labs/derived-wallet-sui";
import { AptosWalletAdapterProvider } from "@aptos-labs/wallet-adapter-react";
import { type PropsWithChildren, useEffect } from "react";

import { restoreWcEvmSession } from "@/contexts/walletconnect-evm-provider";
import { restoreWcSolanaSession } from "@/contexts/walletconnect-solana-provider";
import { env } from "@/env";
import { useKeylessAccounts } from "@/hooks/use-keyless-accounts";
import { useOverrideWallet } from "@/hooks/use-override-wallet";
import { Logger } from "@/utils/logger";

import { checkNetworkReset } from "./check-network-reset";
import { DecibelWalletProvider } from "./decibel-wallet-provider";
import { getSdkConfig, getSdkNetworkName, network } from "./dex-sdk";
import { SuiAccountMismatchModal } from "./sui-account-mismatch-modal";
import { ViewAsProvider } from "./view-as-context";
import { WalletMismatchModal } from "./wallet-mismatch-modal";
import { WriteDexSdkContextProvider } from "./write-dex-sdk-context";

try {
  setupAutomaticEthereumWalletDerivation({ defaultNetwork: network });
  setupAutomaticSolanaWalletDerivation({ defaultNetwork: network });
  setupAutomaticSuiWalletDerivation({ defaultNetwork: network });
} catch (error) {
  Logger.error(error, {
    message: "Failed to setup Ethereum / Solana / Sui wallet derivation",
  });
}

// Check for network resets and clear storage if needed
checkNetworkReset();

export function WalletProvider({ children }: PropsWithChildren) {
  const overrideWallet = useOverrideWallet();
  const { activeKeyless } = useKeylessAccounts();

  const shouldAutoConnect = !overrideWallet && !activeKeyless;

  const networkName = getSdkNetworkName(getSdkConfig());

  useEffect(() => {
    // Ensure Petra tries to connect to the correct network.
    window.PETRA_DEFAULT_NETWORK = networkName;
  }, [networkName]);

  useEffect(() => {
    const wcProjectId = env.NEXT_PUBLIC_WALLETCONNECT_PROJECT_ID;
    if (wcProjectId) {
      void restoreWcEvmSession(wcProjectId);
      void restoreWcSolanaSession(wcProjectId);
    }
  }, []);

  return (
    <AptosWalletAdapterProvider
      autoConnect={shouldAutoConnect}
      dappConfig={{
        network,
        crossChainWallets: true,
      }}
    >
      <ViewAsProvider>
        <DecibelWalletProvider>
          <WriteDexSdkContextProvider>
            {children}
            <WalletMismatchModal />
            <SuiAccountMismatchModal />
          </WriteDexSdkContextProvider>
        </DecibelWalletProvider>
      </ViewAsProvider>
    </AptosWalletAdapterProvider>
  );
}
